/**
 * useScriptEditorState Hook - MA-ARCH-163
 * 
 * Extracted from ScriptEdit.jsx - Manages core editor state including
 * node selection, dirty detection, and derived selectors.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { applyAutoProspectAssignment } from '../utils/scriptEditUtils';

/**
 * Hook to manage the core script editor state
 * @param {string} scriptId - The script ID
 * @param {boolean} canEdit - Whether the user can edit
 */
export function useScriptEditorState(scriptId, canEdit) {
  // Core graph state
  const [graph, setGraph] = useState(null);
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Script name editing
  const [scriptName, setScriptName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Node editing state
  const [nodeTitle, setNodeTitle] = useState("");
  const [yourReply, setYourReply] = useState("");
  const [scriptBlocks, setScriptBlocks] = useState([
    {
      id: crypto.randomUUID(),
      scope: "household",
      blocks: [{ id: crypto.randomUUID(), type: "text", text: "" }],
      __seededFromLegacy: true,
    },
  ]);
  const [coachingNote, setCoachingNote] = useState("");
  const [nodeScope, setNodeScope] = useState("general");

  // Node order state
  const [localNodes, setLocalNodes] = useState(null);
  const [isNodeOrderDirty, setIsNodeOrderDirty] = useState(false);

  // Refs for textarea cursor positioning
  const textAreaRefs = useRef({});

  // Fetch graph
  const fetchGraph = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/scripts/${scriptId}/nodes`);
      setGraph(res.data);
      const start = res.data?.script?.start_node_id;
      const firstNode = res.data?.nodes?.[0]?.node?.id;
      setActiveNodeId((prev) => prev || start || firstNode || null);
    } catch (e) {
      toast({
        title: "Failed to load script",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
      return { error: e, status: e?.response?.status };
    } finally {
      setLoading(false);
    }
    return { success: true };
  }, [scriptId]);

  // Initial fetch
  useEffect(() => {
    fetchGraph();
  }, [fetchGraph]);

  // Derived: active node wrapper
  const active = useMemo(() => {
    const found = graph?.nodes?.find((n) => n.node.id === activeNodeId);
    return found || null;
  }, [graph, activeNodeId]);

  // Derived: is start node
  const isStart = useMemo(() => {
    return active?.node?.id === graph?.script?.start_node_id;
  }, [active, graph?.script?.start_node_id]);

  // Derived: is greeting node
  const isGreeting = useMemo(() => {
    return (
      graph?.script?.greeting_node_id === active?.node?.id ||
      active?.node?.node_type === "greeting"
    );
  }, [active, graph?.script?.greeting_node_id]);

  // Derived: nodes list
  const nodesList = useMemo(() => graph?.nodes || [], [graph]);
  const orderedNodesList = localNodes || nodesList;

  // Derived: node position map
  const nodePositionById = useMemo(() => {
    const m = {};
    (orderedNodesList || []).forEach((wrap, idx) => {
      m[wrap.node.id] = idx + 1;
    });
    return m;
  }, [orderedNodesList]);

  // Sync local nodes with graph (when not dirty)
  useEffect(() => {
    if (isNodeOrderDirty) return;
    setLocalNodes(graph?.nodes || null);
  }, [graph?.nodes, isNodeOrderDirty]);

  // Sync node editing state when active node changes
  useEffect(() => {
    if (!active) return;
    setNodeTitle(active.node.title || "");
    setYourReply(active.node.your_reply || "");

    if (active.node.script_blocks?.length) {
      setScriptBlocks(applyAutoProspectAssignment(active.node.script_blocks));
    } else {
      const legacy = [
        {
          id: crypto.randomUUID(),
          scope: "household",
          blocks: [
            {
              id: crypto.randomUUID(),
              type: "text",
              text: active.node.your_reply || "",
            },
          ],
          __seededFromLegacy: true,
        },
      ];
      setScriptBlocks(applyAutoProspectAssignment(legacy));
    }

    setCoachingNote(active.node.coaching_note || "");
    setNodeScope(active.node.scope || "general");
  }, [active]);

  // Sync script name when graph loads
  useEffect(() => {
    if (!graph?.script) return;
    setScriptName(graph.script.name || "");
  }, [graph?.script?.name]);

  // Insert token into text block
  const insertTokenIntoTextBlock = useCallback((blockId, token) => {
    setScriptBlocks((prev) =>
      prev.map((sb) => ({
        ...sb,
        blocks: (sb.blocks || []).map((x) => {
          if (x.id !== blockId) return x;
          if (x.type !== "text") return x;

          const t = x.text || "";
          const el = textAreaRefs.current?.[blockId];
          const start = typeof el?.selectionStart === "number" ? el.selectionStart : t.length;
          const end = typeof el?.selectionEnd === "number" ? el.selectionEnd : t.length;
          const updated = t.slice(0, start) + token + t.slice(end);

          setTimeout(() => {
            const el2 = textAreaRefs.current?.[blockId];
            if (el2 && typeof el2.setSelectionRange === "function") {
              el2.focus();
              el2.setSelectionRange(start + token.length, start + token.length);
            }
          }, 0);

          return { ...x, text: updated };
        }),
      })),
    );
  }, []);

  // Auto-save draft if dirty
  const autoSaveDraftIfDirty = useCallback(async () => {
    if (!canEdit) return false;

    const ops = [];

    // Save script title if changed
    const currentName = graph?.script?.name || "";
    const nextName = scriptName.trim();
    if (nextName && nextName !== currentName) {
      ops.push(api.put(`/scripts/${scriptId}`, { name: nextName }));
    }

    // Save node content if changed
    if (active?.node) {
      const nodeChanged =
        (nodeTitle || "") !== (active.node.title || "") ||
        (yourReply || "") !== (active.node.your_reply || "") ||
        (coachingNote || "") !== (active.node.coaching_note || "") ||
        JSON.stringify(scriptBlocks || []) !== JSON.stringify(active.node.script_blocks || []) ||
        (nodeScope || "general") !== (active.node.scope || "general");

      if (nodeChanged) {
        ops.push(
          api.put(`/nodes/${active.node.id}`, {
            title: nodeTitle || null,
            your_reply: yourReply,
            script_blocks: scriptBlocks,
            coaching_note: coachingNote,
            scope: nodeScope,
          })
        );
      }
    }

    if (ops.length === 0) return false;

    await Promise.all(ops);
    await fetchGraph();
    toast({ title: "Saved" });
    return true;
  }, [
    canEdit,
    graph?.script?.name,
    scriptName,
    scriptId,
    active,
    nodeTitle,
    yourReply,
    coachingNote,
    scriptBlocks,
    nodeScope,
    fetchGraph,
  ]);

  return {
    // Core state
    graph,
    setGraph,
    loading,
    activeNodeId,
    setActiveNodeId,

    // Script name
    scriptName,
    setScriptName,
    isRenaming,
    setIsRenaming,

    // Node editing
    nodeTitle,
    setNodeTitle,
    yourReply,
    setYourReply,
    scriptBlocks,
    setScriptBlocks,
    coachingNote,
    setCoachingNote,
    nodeScope,
    setNodeScope,

    // Node order
    localNodes,
    setLocalNodes,
    isNodeOrderDirty,
    setIsNodeOrderDirty,

    // Refs
    textAreaRefs,

    // Derived
    active,
    isStart,
    isGreeting,
    nodesList,
    orderedNodesList,
    nodePositionById,

    // Actions
    fetchGraph,
    insertTokenIntoTextBlock,
    autoSaveDraftIfDirty,
  };
}

export default useScriptEditorState;
